import { useEffect, useState } from 'react'
import * as zod from 'zod'
import { useCustomForm } from '@mote/helpers/hooks/business'
import { Alert } from '../Alert'
import type { DialogProps } from '../DialogV2'
import { Dialog } from '../DialogV2'
import { Button } from '../button'
import { FormInput } from '../form'

const formSchema = zod.object({
  moteTitle: zod
    .string({ invalid_type_error: 'Please enter at least one character' })
    .min(1, { message: 'Please enter a name for your mote' })
})

export interface MoteSaveToMotebookDialogProps
  extends Omit<DialogProps, 'children'> {
  title: string
  onSave: (title: string) => Promise<any>
}

export const MoteSaveToMotebookDialog = (
  props: MoteSaveToMotebookDialogProps
): JSX.Element => {
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    formState: { isValid },
    ...form
  } = useCustomForm({
    schema: formSchema,
    defaultValues: {
      moteTitle: props.title
    }
  })

  useEffect(() => {
    if (props.title) {
      form.trigger('moteTitle')
    }
  }, [])

  function cleanup() {
    setIsSaving(false)
    setError(null)
    form.reset()
  }

  function handleOnClose() {
    cleanup()
    props.onClose()
  }

  async function submitForm() {
    setIsSaving(true)
    const formValues = form.getValues()
    try {
      const response = await props.onSave(formValues.moteTitle)
      setIsSaving(false)

      if (response.error) {
        return
      }

      handleOnClose()
    } catch {
      setError(
        'Something went wrong while processing your request. Please try again later.'
      )
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      {...{
        maxWidth: 420,
        ...props,
        onClose: handleOnClose,
        id: 'save-to-motebook'
      }}
    >
      <Dialog.Header>Save to motebook</Dialog.Header>
      <Dialog.Content as='form' onSubmit={form.handleSubmit(submitForm)}>
        {error && <Alert type='error' message={error} mb={3} w='auto' />}
        <FormInput
          label=''
          name='moteTitle'
          placeholder='Give your mote a name'
          control={form.control}
          required
        />
      </Dialog.Content>
      <Dialog.Footer>
        <Button
          variant='secondary'
          onClick={() => handleOnClose()}
          text='Cancel'
        />
        <Button
          type='submit'
          text='Save'
          disabled={!isValid}
          loading={isSaving}
          loadingText='Saving ...'
          form='mote-dialog--content-save-to-motebook'
        />
      </Dialog.Footer>
    </Dialog>
  )
}
